import {observable, reaction, runInAction} from "mobx";
import {getCelloToken, sendCelloAttribution} from "./requests";
import {getLanguage} from "i18n/LanguageProvider";
import {observedLanguage} from "i18n/translator";
import {paramsRemove, validateEmail} from "./utils";
import {getCookie} from "utils/abTest";
import {getStored} from "./persistence";
import {CELLO_LOCALES, CELLO_SCRIPT_URL, DEFAULT_LANGUAGE_CODE} from "./config";
import {IUserSettings} from "./types";

// Cello referral integration (docs.cello.so): the header referral widget for
// registered users, and the referral code (?ucc=) a visitor arrived with,
// reported to the backend, which owns the attribution rules.

// The launcher shows as soon as the SDK is loaded and the account has a
// programme; the click boots on demand (openCello), so a failed boot never
// costs the button.
export const celloEnabled = observable.box(false);

// The SDK drains window.cello.cmd itself; a broken one would hang the boot.
const BOOT_TIMEOUT_MS = 20_000;

const PENDING_KEY = "cello_ucc_pending";
const SENT_KEY = "cello_ucc_sent";

// Storage unavailable (blocked, partitioned) must cost only persistence, never
// the visit; reads go through getStored for the same reason.
const persist = (key: string, value: string | null) => {
    try {
        value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value);
    } catch (e) { /* the in-memory state still serves this visit */ }
};

// Snapshotted at module load: the scan page mirrors the live mode selection
// into the URL right after mount, wiping arrival params before anything async
// can read them (same reason init.js snapshots INCOMING_SCAN_MODES). Persisted
// so an anon's referral code survives until they actually sign up.
const arrivalUcc = new URLSearchParams(window.location.search).get("ucc");
if (arrivalUcc) persist(PENDING_KEY, arrivalUcc);

const loadScript = (src: string) =>
    new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.type = "module";
        script.async = true;
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
    });

// One SDK script tag per page, however many boots ask for it; a failed load is
// not kept, so the next boot retries.
let sdkLoad: Promise<void> | null = null;
const loadSdk = () => {
    if (!sdkLoad) {
        sdkLoad = loadScript(CELLO_SCRIPT_URL).catch((e) => {
            sdkLoad = null;
            throw e;
        });
    }
    return sdkLoad;
};

// One run of `work` per account per page, shared by the callers that overlap
// (init re-runs on every auth transition). A failure is not kept, so a later
// call retries; a different account starts over.
const oncePerUser = (work: (user: IUserSettings) => Promise<void>) => {
    let promise: Promise<void> | null = null;
    let forUser: string | null = null;
    return (user: IUserSettings) => {
        const key = user.uuid ?? null;
        if (!promise || forUser !== key) {
            forUser = key;
            promise = work(user).catch((e) => {
                promise = null;
                throw e;
            });
        }
        return promise;
    };
};

// The arrival snapshot wins (a fresh referral click; also what the landing
// bridges across origins), then the persisted pending code, then the cookie
// Cello's attribution script (the landing loads it) set on an earlier visit —
// read directly, the way the landing reads it, not through their script.
const capturedUcc = (): string | null =>
    arrivalUcc || getStored(PENDING_KEY) || getCookie("cello-referral") || null;

// The backend is the judge (set-once, signup-window gate); this only avoids
// re-sending the same code on every app open. Scoped to the account, not the
// browser: the same referral link opened by an anon and then by the account
// they sign up as is two different attributions.
const reportAttribution = oncePerUser(async (user) => {
    const ucc = capturedUcc();
    if (!ucc) return;
    const stamp = `${user.uuid}:${ucc}`;
    if (getStored(SENT_KEY) !== stamp) {
        await sendCelloAttribution(ucc);
        persist(SENT_KEY, stamp);
    }
    // An anon's code has to outlive the anon: the account they sign up as
    // reports it again, and an OAuth login reloads the page in between.
    if (!user.is_anon) persist(PENDING_KEY, null);
});

// Cello scans the DOM for the launcher once, at boot, and never rescans — so
// boot waits for the header to mount. Timing out costs only Cello's own badge
// on the button: the click itself is bound here, not by their DOM scan.
const waitForLauncher = (timeoutMs = 8000) =>
    new Promise<void>((resolve) => {
        const started = Date.now();
        const tick = () => {
            if (document.getElementById("cello-launcher") || Date.now() - started > timeoutMs) {
                resolve();
                return;
            }
            setTimeout(tick, 200);
        };
        tick();
    });

const withTimeout = <T>(work: Promise<T>, ms: number, what: string) =>
    Promise.race([
        work,
        new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${what} timed out`)), ms)),
    ]);

// The SDK takes one boot per page: a second `cello.boot` is refused ("Cello is
// already booted") and its promise never settles. So this promise settles on the
// SDK's word alone — the callers' waits (withTimeout) race it from outside and
// leave it be, and a boot that outlives a wait is still the boot everyone waits
// on afterwards. Only a failure of our own (token, script, a boot the SDK
// rejected) lets go of it, so the next call can try again.
const bootWidget = oncePerUser(async (user) => {
    // The token fetch and the script load are independent — run them together.
    const [config] = await Promise.all([getCelloToken(), loadSdk()]);
    if (!config.enabled) return;
    // Enough to offer the button: the SDK is here and this account has a
    // programme. Waiting for the boot below would hide it for seconds.
    runInAction(() => celloEnabled.set(true));
    await waitForLauncher();
    const w = window as any;
    w.cello = w.cello || {cmd: []};
    const language = celloLanguage();
    await new Promise<void>((resolve, reject) => {
        w.cello.cmd.push(async (cello: any) => {
            try {
                await cello.boot({
                    productId: config.product_id,
                    token: config.token,
                    language,
                    productUserDetails: {
                        email: validateEmail(user.username) ? user.username : undefined,
                        fullName: user.name || undefined,
                    },
                });
                resolve();
            } catch (e) {
                reject(e);
            }
        });
    });
    widgetLanguage = language;
    // The UI language may have changed while the boot was in flight.
    void syncWidgetLanguage();
});

// Cello has no Arabic (CELLO_LOCALES): those users get the default language.
const celloLanguage = () => {
    const lang = getLanguage();
    return CELLO_LOCALES.includes(lang) ? lang : DEFAULT_LANGUAGE_CODE;
};

// The language the widget runs in; null until a boot succeeds.
let widgetLanguage: string | null = null;

// Switching the language doesn't reload the page, and the SDK boots once per page —
// so a booted widget is switched at runtime.
const syncWidgetLanguage = async () => {
    const language = celloLanguage();
    if (widgetLanguage === null || widgetLanguage === language) return;
    widgetLanguage = language;
    try {
        await (window as any).Cello?.("changeLanguage", language);
    } catch (e) {
        console.error("Cello language change failed", e);
    }
};
reaction(observedLanguage, () => void syncWidgetLanguage());

// Who the launcher boots the widget for; null while the session is anonymous.
let widgetUser: IUserSettings | null = null;

/** Opens the referral widget, booting it first when the click arrived before
 *  the boot did (or after one failed) — so the button never does nothing. A wait
 *  that fails doesn't cancel the open either: the SDK may well have booted by
 *  then, and "open" is all a booted SDK needs. */
export const openCello = async () => {
    try {
        if (widgetUser) await withTimeout(bootWidget(widgetUser), BOOT_TIMEOUT_MS, "Cello boot");
    } catch (e) {
        console.error("Cello boot failed", e);
    }
    try {
        await (window as any).Cello?.("open");
    } catch (e) {
        console.error("Cello open failed", e);
    }
};

// Entry point, called from init on every authenticated session. Anons report
// too: the code parks on their backend row, so a guest checkout's Stripe
// customer carries it and the claim hands it to the account that signs up —
// even from another browser (the claim email link). The widget itself is for
// registered users only, and its boot owes nothing to the report, so the two
// run side by side.
export const initCello = async (user: IUserSettings | null) => {
    if (!user) return;
    const bootable = !user.is_anon && !!CELLO_SCRIPT_URL;
    widgetUser = bootable ? user : null;
    if (!bootable) runInAction(() => celloEnabled.set(false));
    // Snapshotted and persisted above, so the URL can be clean before the
    // report — a bookmarked or shared URL must not carry someone's code.
    paramsRemove("ucc");
    try {
        await Promise.all([
            reportAttribution(user),
            bootable ? withTimeout(bootWidget(user), BOOT_TIMEOUT_MS, "Cello boot") : undefined,
        ]);
    } catch (e) {
        console.error("Cello init failed", e);
    }
};
